import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useAuth } from "@contexts/auth/AuthProvider";
import { RecommendedConditions } from "@types-runway/case";
import { FlattenedPage } from "@types-runway/questionnaire";
import { Partner, RootState } from "@types-runway/state";
import {
  AskedScreensContext,
  selectAskedScreens,
  selectCountedScreens,
} from "@utils/questionnaire/progress/askedScreens";
import { deriveTimeLeft } from "@utils/questionnaire/progress/timeLeft";
import {
  deriveSectionSegments,
  SectionSegment,
} from "@utils/questionnaire/progress/sectionProgress";

interface QuestionnaireProgressOptions {
  flattenedPages: FlattenedPage[];
  selectedStateKey: string;
  partner: Partner | null;
  pageIsLoading: boolean;
  addonRecommendations: RecommendedConditions | null;
}

interface QuestionnaireProgress {
  segments: SectionSegment[];
  timeLeftLabel: string;
  shownMinutesLeft: number | null;
  questionsLeft: number;
  // False while the questionnaire passes through a screen its rules skip.
  isCurrentScreenAsked: boolean;
}

const positionOf = (pages: FlattenedPage[], stateKey: string): number =>
  pages.findIndex((page) => page.stateKey === stateKey);

const areAddOnsSkipped = (
  pages: FlattenedPage[],
  selectedStateKey: string,
  {
    pageIsLoading,
    addonRecommendations,
  }: Pick<QuestionnaireProgressOptions, "pageIsLoading" | "addonRecommendations">
): boolean => {
  if (pageIsLoading) return false;
  if (addonRecommendations) return addonRecommendations.conditions.length === 0;

  const addOnsPosition = positionOf(pages, "addOns");

  return addOnsPosition >= 0 && positionOf(pages, selectedStateKey) > addOnsPosition;
};

export const useQuestionnaireProgress = ({
  flattenedPages,
  selectedStateKey,
  partner,
  pageIsLoading,
  addonRecommendations,
}: QuestionnaireProgressOptions): QuestionnaireProgress => {
  const { isAuth } = useAuth();
  const questions = useSelector((state: RootState) => state.questions);
  const user = useSelector((state: RootState) => state.user.data);
  const conditions = useSelector((state: RootState) => state.case.newCase.data.conditions);

  return useMemo(() => {
    const effectiveStateKey = pageIsLoading ? "addOns" : selectedStateKey;
    const screensContext: AskedScreensContext = {
      questions,
      isAuth,
      partner,
      // Read at call time like shouldRender; ENG-743 shares the rules.
      query: new URLSearchParams(window.location.search),
      dateOfBirth: user.date_of_birth?.toString() ?? "",
      conditions,
      user,
      areAddOnsEmpty: areAddOnsSkipped(flattenedPages, selectedStateKey, {
        pageIsLoading,
        addonRecommendations,
      }),
    };
    const applicableScreens = new Set(selectAskedScreens(flattenedPages, screensContext));
    const possibleScreens = new Set(selectCountedScreens(flattenedPages, screensContext));
    // Only the section bar uses the counted screens; screens left and the time left stay on the asked screens.
    const countedScreens = flattenedPages.filter(
      (page) => page.stateKey === effectiveStateKey || possibleScreens.has(page)
    );
    const askedScreens = flattenedPages.filter(
      (page) => page.stateKey === effectiveStateKey || applicableScreens.has(page)
    );
    const screensLeft = askedScreens.slice(positionOf(askedScreens, effectiveStateKey));
    const timeLeft = deriveTimeLeft(screensLeft, { isAwaitingRecommendations: pageIsLoading });
    const currentScreen = flattenedPages.find((page) => page.stateKey === selectedStateKey);

    return {
      segments: deriveSectionSegments(countedScreens, effectiveStateKey),
      timeLeftLabel: timeLeft.label,
      shownMinutesLeft: timeLeft.shownMinutes,
      questionsLeft: screensLeft.length,
      isCurrentScreenAsked: currentScreen !== undefined && applicableScreens.has(currentScreen),
    };
  }, [
    flattenedPages,
    selectedStateKey,
    partner,
    pageIsLoading,
    addonRecommendations,
    questions,
    isAuth,
    user,
    conditions,
  ]);
};
